import React, { useState, useEffect, useContext } from 'react';
import { t } from 'i18next';
import {
  ProfileHeaderContent,
  EmailChange,
  ProfileInformations,
  PaperlessCommunications,
} from 'templates';
import {
  GlobalSuccess,
  ModalOverlay,
  Headings,
  Paragraph,
  Button,
} from '@sentara-health/optimahealth_web';
import {
  DeviceTypes,
  useDeviceType,
  domainForSegment,
  ANALYTICS_MESSAGES,
  useAnalytics,
} from 'common';
import { GlobalContext } from 'context';

const EmailPaperless = ({
  state,
  expand = true,
  setState,
  onExpand,
  onCollapse,
  memberView = false,
}: {
  state: ProfileInformations;
  setState: React.Dispatch<React.SetStateAction<ProfileInformations>>;
  expand?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  memberView?: boolean;
}) => {
  const [showEdit, setShowEdit] = useState(true);
  const [showPaperless, setShowPaperless] = useState(true);
  const [success, setSuccess] = useState(false);
  const [emailConfirmationModal, setEmailConfirmationModal] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [step, setStep] = useState<1 | 2>(1); // Step 1: EmailChange, Step 2: PaperlessCommunications
  const { getFeature } = useContext(GlobalContext);
  const { trackContentListing } = useAnalytics();

  useEffect(() => {
    setEmail(state?.emailConsent?.email ?? '');
  }, [state?.emailConsent]);

  // Success Message for Email and Paperless Update
  const handleSuccess = () => {
    setSuccess(true);
    setShowEdit(true);
    setEmail(email);
    setStep(1);
  };

  const handleNext = () => {
    setStep(2); // Move to PaperlessCommunications step
  };

  const onClickEditPaperless = () => {
    trackContentListing(
      '100',
      ANALYTICS_MESSAGES.PAPERLESS_EDIT_CLICK,
      17,
      `${domainForSegment}/profile`
    );
    if (!state?.isEmailValid && getFeature?.EmailBounceBack) {
      setEmailConfirmationModal(true);
    } else {
      setEmailConfirmationModal(false);
      setShowEdit(!showEdit);
      setStep(2);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  let containerWidth = '100%';
  if (!isMobile && !showEdit) {
    containerWidth = '70%';
  }

  return (
    <>
      <hr />
      <div>
        <div className={`col-sm-12 col-12`} style={{ width: containerWidth }}>
          <ProfileHeaderContent
            header={t('dashboard.paperlessCommunicationsLabel')}
            contentOne={t('profile.goPaperlessDesc') ?? ''}
            dataTestIdOne="profileName"
            showButton={showEdit}
            onClick={() => {
              onClickEditPaperless();
            }}
            contentClassNameOne={'mb-3 mb-lg-0 mb-md-0'}
            disableButton={!state?.isMemberEnrolled}
          />
        </div>
        {!showEdit ? (
          <div className="communication_preference_wrapper">
            {step === 1 ? (
              <div className="mt-2">
                <ProfileHeaderContent
                  data-testid="profile-header-component"
                  header={t('common.emailAddress')}
                  contentOne={email ?? ''}
                  dataTestIdOne="profileName"
                  contentClassNameOne={'_disabled mb-3 mb-lg-0 mb-md-0'}
                  disableButton={!state?.isMemberEnrolled}
                />
                <EmailChange
                  customButton={true}
                  buttonLabel={t('profile.saveNextStepPaperless') ?? ''}
                  emailId={email ?? ''}
                  onClickCancel={() => {
                    setShowEdit(!showEdit);
                    setStep(1);
                  }}
                  onClickSubmit={() => handleNext()}
                  memberView={memberView}
                />
              </div>
            ) : (
              <div
                style={{
                  animation:
                    state?.email && state?.isEmailValid
                      ? undefined
                      : 'slideLeft 0.3s ease-out',
                }}
              >
                <PaperlessCommunications
                  email={email}
                  setState={setState}
                  state={state}
                  onClickCancel={() => {
                    setShowEdit(!showEdit);
                    setShowPaperless(!showPaperless);
                    setStep(1);
                  }}
                  handleSuccess={handleSuccess}
                />
              </div>
            )}
          </div>
        ) : null}
        {emailConfirmationModal && (
          <ModalOverlay
            isOpen={emailConfirmationModal}
            onClose={() => setEmailConfirmationModal(false)}
            overlayInfo="Modal information"
          >
            <div data-testid="amSurePopUp">
              <Headings
                level={2}
                text={t('profile.EmailRequired')}
                variant="regular"
              />
              {/* Description paragraphs */}
              <Paragraph
                text={t('profile.EmailRequiredDesc')}
                variant="regular"
                data-testId="iamSureContent"
                className="mb-4 pb-2"
              />
              <div className="row d-flex justify-content-start align-items-center py-2">
                <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                  <Button
                    dataTestId={'imSureButton'}
                    variant="primary_btn"
                    type="submit"
                    onClick={() => {
                      setEmailConfirmationModal(false);
                      setShowEdit(!showEdit);
                      setStep(1); // Ensure starting at step 1
                    }}
                  >
                    {t('profile.EmailUpdateBtn')}
                  </Button>
                </div>
                <div className="col-sm-auto col-12 mb-sm-0">
                  <Button
                    dataTestId={'signupButton'}
                    variant="secondary_btn"
                    onClick={() => {
                      setEmailConfirmationModal(false);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </ModalOverlay>
        )}

        {success ? (
          <div className="fade-toast">
            <GlobalSuccess variant="mb-3" dataTestId="usernameServiceErr12">
              {t('profile.optedPaperlessSuccess')}
            </GlobalSuccess>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default EmailPaperless;
